import { ROTULO_PAPEL, type Permissao } from '@baiocchi/shared'
import { Avatar, cn } from '@baiocchi/ui'
import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import { useSessao } from '../autenticacao/contexto-sessao'
import { IconeSair, IconeSeta } from '../componentes/icones'
import { Marca } from './marca'
import { NAVEGACAO, type ItemNavegacao } from './navegacao'

export interface PropriedadesSidebar {
  colapsada: boolean
  /** No celular a sidebar vira gaveta; fechar após navegar. */
  aoNavegar?: () => void
}

/**
 * Sidebar fixa — o padrão observado em todas as referências: avatar do usuário
 * com nome e papel no topo, navegação abaixo, "Sair" no fim.
 *
 * Item cuja permissão o usuário não tem **não aparece**. Um submenu que perde
 * todos os filhos também desaparece: uma seção "Configurações" que abre vazia é
 * pior do que não existir.
 */
export function Sidebar({ colapsada, aoNavegar }: PropriedadesSidebar) {
  const { usuario, pode } = useSessao()
  const local = useLocation()

  const visiveis = NAVEGACAO.map((item) => filtrarPorPermissao(item, pode)).filter(
    (item): item is ItemNavegacao => item !== null,
  )

  return (
    <div className="flex h-full flex-col bg-superficie-inversa text-white/85">
      <div
        className={cn(
          'flex h-topbar shrink-0 items-center border-b border-white/10',
          colapsada ? 'justify-center px-2' : 'px-4',
        )}
      >
        {colapsada ? <Marca variante="compacta" /> : <MarcaNaSidebar />}
      </div>

      {/* Identidade do usuário logado */}
      <div
        className={cn(
          'flex shrink-0 items-center gap-3 border-b border-white/10 py-4',
          colapsada ? 'justify-center px-2' : 'px-4',
        )}
      >
        <Avatar
          nome={usuario?.nome ?? '—'}
          fotoUrl={usuario?.fotoUrl ?? null}
          tamanho={colapsada ? 'md' : 'lg'}
        />
        {!colapsada && usuario && (
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-white">{usuario.nome}</p>
            <p className="truncate text-xs text-white/60">{ROTULO_PAPEL[usuario.papel]}</p>
            {usuario.unidadePrincipal ? (
              <p className="truncate text-[0.6875rem] text-white/45">
                {usuario.unidadePrincipal.titulo}
              </p>
            ) : null}
          </div>
        )}
      </div>

      <nav className="min-h-0 flex-1 overflow-y-auto py-3" aria-label="Navegação principal">
        <ul className="flex flex-col gap-0.5 px-2">
          {visiveis.map((item) => (
            <ItemDeMenu
              key={item.para}
              item={item}
              colapsada={colapsada}
              caminhoAtual={local.pathname}
              aoNavegar={aoNavegar}
            />
          ))}
        </ul>
      </nav>

      <div className="shrink-0 border-t border-white/10 p-2">
        <NavLink
          to="/sair"
          onClick={aoNavegar}
          className={cn(
            'flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-white/70 transition-colors hover:bg-white/10 hover:text-white',
            colapsada && 'justify-center px-2',
          )}
        >
          <IconeSair className="h-5 w-5 shrink-0" />
          {!colapsada && 'Sair'}
        </NavLink>
      </div>
    </div>
  )
}

function MarcaNaSidebar() {
  return (
    <span className="inline-flex items-center gap-2.5">
      <img
        src="/logo.png"
        alt=""
        aria-hidden="true"
        className="h-8 w-8 rounded-md bg-white object-cover"
        style={{ objectPosition: '50% 32%' }}
      />
      <span className="flex flex-col leading-none">
        <span className="text-sm font-bold uppercase tracking-wide text-white">Baiocchi</span>
        <span className="text-[0.625rem] font-semibold uppercase tracking-[0.2em] text-marca-400">
          Leads
        </span>
      </span>
    </span>
  )
}

type TestePermissao = (...permissoes: ReadonlyArray<Permissao>) => boolean

function filtrarPorPermissao(item: ItemNavegacao, pode: TestePermissao): ItemNavegacao | null {
  const permitido = (permissoes?: ReadonlyArray<Permissao>) =>
    !permissoes || permissoes.length === 0 || pode(...permissoes)

  if (!item.filhos) return permitido(item.permissoes) ? item : null

  const filhos = item.filhos.filter((filho) => permitido(filho.permissoes))
  // Seção pai só sobrevive se algum filho sobreviveu — ou se ela própria é
  // navegável e permitida.
  if (filhos.length === 0) return null
  return { ...item, filhos }
}

function ItemDeMenu({
  item,
  colapsada,
  caminhoAtual,
  aoNavegar,
}: {
  item: ItemNavegacao
  colapsada: boolean
  caminhoAtual: string
  aoNavegar?: () => void
}) {
  const dentroDaSecao = caminhoAtual.startsWith(item.para) && item.para !== '/'
  const [aberto, setAberto] = useState(dentroDaSecao)
  const Icone = item.icone

  const classesBase =
    'flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors'

  if (item.filhos && item.filhos.length > 0) {
    return (
      <li>
        <button
          type="button"
          onClick={() => setAberto((valor) => !valor)}
          aria-expanded={aberto}
          className={cn(
            classesBase,
            dentroDaSecao ? 'bg-white/10 text-white' : 'text-white/70 hover:bg-white/5 hover:text-white',
            colapsada && 'justify-center px-2',
          )}
        >
          <Icone className="h-5 w-5 shrink-0" />
          {!colapsada && (
            <>
              <span className="flex-1 text-left">{item.rotulo}</span>
              <IconeSeta
                className={cn('h-4 w-4 shrink-0 transition-transform', aberto && 'rotate-90')}
              />
            </>
          )}
        </button>

        {aberto && !colapsada && (
          <ul className="mb-1 ml-[1.6rem] mt-0.5 flex flex-col gap-0.5 border-l border-white/10 pl-3">
            {item.filhos.map((filho) => (
              <li key={filho.para}>
                <NavLink
                  to={filho.para}
                  onClick={aoNavegar}
                  className={({ isActive }) =>
                    cn(
                      'block rounded-md px-3 py-2 text-[0.8125rem] transition-colors',
                      isActive
                        ? 'bg-marca-500/20 font-medium text-marca-300'
                        : 'text-white/60 hover:bg-white/5 hover:text-white',
                    )
                  }
                >
                  {filho.rotulo}
                </NavLink>
              </li>
            ))}
          </ul>
        )}
      </li>
    )
  }

  return (
    <li>
      <NavLink
        to={item.para}
        end={item.para === '/'}
        onClick={aoNavegar}
        title={colapsada ? item.rotulo : undefined}
        className={({ isActive }) =>
          cn(
            classesBase,
            isActive
              ? 'bg-marca-500 font-medium text-white'
              : 'text-white/70 hover:bg-white/5 hover:text-white',
            colapsada && 'justify-center px-2',
          )
        }
      >
        <Icone className="h-5 w-5 shrink-0" />
        {!colapsada && item.rotulo}
      </NavLink>
    </li>
  )
}
